import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Field } from "@/components/common";
import { CompanyLogo } from "@/components/common/CompanyLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Mail, Search, Sparkles, Building2, User, FileText, ArrowRight, RotateCcw, AlertTriangle, Link2, ExternalLink, CheckCircle2, Globe } from "lucide-react";
import { toast } from "sonner";

const EMAIL_PLACEHOLDER = "Paste the full email enquiry here - sender name, signature and the request. The AI pulls out the company, the contact and what they want, then looks up any missing details (website, phone, address) on the web.";
const DESCRIBE_PLACEHOLDER = "Describe the company or enquiry in your own words, e.g. 'Northwind Foods in Leeds asked about an ERP rollout before Q3'. The AI searches the web to fill in the company details.";

// Small badge shown next to any field the AI filled in from a web lookup, so the reviewer knows to double-check it.
const WebTag = () => (
  <span className="inline-flex items-center gap-0.5 ml-1.5 text-[10px] font-medium text-primary align-middle" data-testid="capture-web-tag">
    <Globe className="h-3 w-3" />web
  </span>
);

// Amber badge marking a field the AI was not confident about and the reviewer should verify.
const VerifyTag = () => (
  <span className="inline-flex items-center gap-0.5 ml-1.5 text-[10px] font-medium text-[hsl(var(--warning))] align-middle" data-testid="capture-verify-tag">
    <AlertTriangle className="h-3 w-3" />verify
  </span>
);

export default function CapturePage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("email");
  const [emailText, setEmailText] = useState("");
  const [describeText, setDescribeText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [lead, setLead] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [sources, setSources] = useState([]);
  const [enriched, setEnriched] = useState([]);
  const [uncertain, setUncertain] = useState([]);
  const [aiMeta, setAiMeta] = useState(null);
  const [createOpp, setCreateOpp] = useState(true);
  const [icpId, setIcpId] = useState("none");
  const [icps, setIcps] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.get("/icps").then((r) => setIcps(r.data || [])).catch(() => {}); }, []);

  const reset = () => { setLead(null); setWarnings([]); setSources([]); setEnriched([]); setUncertain([]); setAiMeta(null); };

  const parse = async () => {
    const text = mode === "email" ? emailText : describeText;
    if (text.trim().length < 8) { toast.error("Add a bit more detail first."); return; }
    setParsing(true); reset();
    try {
      const { data } = await api.post("/intake/parse", { mode, text });
      const L = data.lead;
      setLead({
        companyName: L.company.name || "", website: L.company.website || "", location: L.company.location || "",
        address: L.company.address || "", country: L.company.country || "", industry: L.company.industry || "", companySummary: L.company.summary || "",
        contactName: L.contact.name || "", role: L.contact.role || "", email: L.contact.email || "", phone: L.contact.phone || "", linkedin: L.contact.linkedin_url || "",
        enquiry: L.enquiry.summary || "", products: (L.enquiry.products || []).join(", "), urgency: L.enquiry.urgency || "medium", nextAction: L.enquiry.next_action || "",
      });
      setWarnings(data.warnings || []); setSources(data.lead.sources || []); setEnriched(data.lead.enriched_fields || []); setUncertain(data.lead.uncertain_fields || []); setAiMeta({ source: data.ai_source, cost: data.ai_cost_usd });
      if (!L.company.name) toast.warning("Couldn't detect a company name - please fill it in.");
    } catch (e) { toast.error(errMsg(e)); } finally { setParsing(false); }
  };

  const set = (k) => (e) => setLead((l) => ({ ...l, [k]: e.target.value }));
  // label helper: appends a "web" badge when the AI filled this field from a web lookup, and a "verify" badge when unsure
  const lbl = (text, path) => (<>{text}{enriched.includes(path) && <WebTag />}{uncertain.includes(path) && <VerifyTag />}</>);
  const blocked = warnings.some((w) => w.type === "suppressed");

  const createLead = async () => {
    if (!lead.companyName.trim()) { toast.error("A company name is required."); return; }
    setCreating(true);
    try {
      const payload = {
        company: { name: lead.companyName, website: lead.website || null, location: lead.location || null, address: lead.address || null, country: lead.country || null, industry: lead.industry || null, summary: lead.companySummary || null },
        contact: lead.contactName ? { name: lead.contactName, role: lead.role || null, email: lead.email || null, phone: lead.phone || null, linkedin_url: lead.linkedin || null } : null,
        enquiry_summary: lead.enquiry || null, products: lead.products ? lead.products.split(",").map((s) => s.trim()).filter(Boolean) : [],
        next_action: lead.nextAction || null, urgency: lead.urgency, create_opportunity: createOpp,
        icp_id: icpId === "none" ? null : icpId, source_label: mode === "email" ? "Lead capture (email)" : "Lead capture (described)",
      };
      const { data } = await api.post("/intake/create", payload);
      toast.success(data.linked_existing ? "Linked to the existing account" : "Lead imported into the pipeline");
      if (data.opportunity_id) navigate(`/pipeline?open=${data.opportunity_id}`);
      else navigate(`/accounts?open=${data.account_id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setCreating(false); }
  };

  return (
    <div className="page max-w-5xl">
      <PageHeader title="Capture lead" subtitle="Paste an email enquiry or describe a company. The AI builds a ready-to-import lead - review it, then add it to your pipeline." />

      {!lead && (
        <div className="panel p-4 sm:p-5 max-w-3xl">
          <Tabs value={mode} onValueChange={(v) => { setMode(v); reset(); }}>
            <TabsList><TabsTrigger value="email" data-testid="capture-tab-email"><Mail className="h-4 w-4 mr-1.5" />Paste email</TabsTrigger><TabsTrigger value="describe" data-testid="capture-tab-describe"><Search className="h-4 w-4 mr-1.5" />Describe a company</TabsTrigger></TabsList>
            <TabsContent value="email" className="pt-4 space-y-3">
              <Textarea rows={9} value={emailText} onChange={(e) => setEmailText(e.target.value)} placeholder={EMAIL_PLACEHOLDER} data-testid="capture-email-input" />
              <p className="meta flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" />Reads the email, then looks up missing company details on the web. Contact emails and phones are only taken from official public pages, never guessed.</p>
            </TabsContent>
            <TabsContent value="describe" className="pt-4 space-y-3">
              <Textarea rows={5} value={describeText} onChange={(e) => setDescribeText(e.target.value)} placeholder={DESCRIBE_PLACEHOLDER} data-testid="capture-describe-input" />
              <p className="meta flex items-center gap-1.5"><Search className="h-3.5 w-3.5" />Searches the web for the company's public details. Contact emails are never guessed.</p>
            </TabsContent>
          </Tabs>
          <div className="flex justify-end mt-4">
            <Button onClick={parse} disabled={parsing} data-testid="capture-parse-button">
              <Sparkles className="h-4 w-4 mr-2" />{parsing ? (mode === "describe" ? "Researching…" : "Reading…") : (mode === "describe" ? "Look up & build lead" : "Build lead from email")}
            </Button>
          </div>
          {parsing && mode === "describe" && <p className="meta text-center mt-2">Searching the web and extracting details - this can take a few seconds.</p>}
        </div>
      )}

      {lead && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />Review the details below, edit anything, then import.{aiMeta && <span className="meta">· AI: {aiMeta.source}</span>}</div>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="capture-startover"><RotateCcw className="h-4 w-4 mr-1.5" />Start over</Button>
          </div>

          {(enriched.length > 0 || uncertain.length > 0) && (
            <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] p-3 text-sm flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid="capture-enrich-legend">
              {enriched.length > 0 && <span className="flex items-start gap-2"><Globe className="h-4 w-4 mt-0.5 shrink-0 text-primary" /><span>Fields marked <span className="text-primary font-medium">web</span> were filled from a web lookup.</span></span>}
              {uncertain.length > 0 && <span className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--warning))]" /><span>Fields marked <span className="text-[hsl(var(--warning))] font-medium">verify</span> were uncertain - please double-check before importing.</span></span>}
            </div>
          )}

          {warnings.map((w, i) => (
            <div key={i} className={`rounded-md border p-3 text-sm flex items-start gap-2 ${w.type === "suppressed" ? "border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger-bg,var(--danger)/0.08))]" : "border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.08)]"}`} data-testid={`capture-warning-${w.type}`}>
              {w.type === "duplicate" ? <Link2 className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}<span>{w.message}</span>
            </div>
          ))}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="panel p-4 space-y-3">
              <div className="section-title flex items-center gap-2"><CompanyLogo website={lead.website} name={lead.companyName} size="md" /><span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" />Company</span></div>
              <Field label={lbl("Company name *", "company.name")}><Input value={lead.companyName} onChange={set("companyName")} data-testid="capture-company-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={lbl("Website", "company.website")}><Input value={lead.website} onChange={set("website")} placeholder="example.com" data-testid="capture-website" /></Field>
                <Field label={lbl("Industry", "company.industry")}><Input value={lead.industry} onChange={set("industry")} data-testid="capture-industry" /></Field>
                <Field label={lbl("Location", "company.location")}><Input value={lead.location} onChange={set("location")} /></Field>
                <Field label={lbl("Country", "company.country")}><Input value={lead.country} onChange={set("country")} /></Field>
              </div>
              <Field label={lbl("Address", "company.address")}><Input value={lead.address} onChange={set("address")} placeholder="Street, city, postcode" data-testid="capture-address" /></Field>
              <Field label={lbl("Summary", "company.summary")}><Textarea rows={3} value={lead.companySummary} onChange={set("companySummary")} /></Field>
            </div>

            <div className="panel p-4 space-y-3">
              <div className="section-title flex items-center gap-1.5"><User className="h-4 w-4" />Primary contact</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={lbl("Name", "contact.name")}><Input value={lead.contactName} onChange={set("contactName")} data-testid="capture-contact-name" /></Field>
                <Field label={lbl("Role", "contact.role")}><Input value={lead.role} onChange={set("role")} /></Field>
                <Field label={lbl("Email", "contact.email")}><Input value={lead.email} onChange={set("email")} data-testid="capture-contact-email" /></Field>
                <Field label={lbl("Phone", "contact.phone")}><Input value={lead.phone} onChange={set("phone")} data-testid="capture-contact-phone" /></Field>
              </div>
              <Field label={lbl("LinkedIn", "contact.linkedin_url")}><Input value={lead.linkedin} onChange={set("linkedin")} /></Field>
              {!lead.contactName && <p className="meta">No contact yet - you can leave this blank and add one later.</p>}
            </div>
          </div>

          <div className="panel p-4 space-y-3">
            <div className="section-title flex items-center gap-1.5"><FileText className="h-4 w-4" />Enquiry</div>
            <Field label="What they want"><Textarea rows={2} value={lead.enquiry} onChange={set("enquiry")} data-testid="capture-enquiry" /></Field>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Products / services of interest (comma separated)"><Input value={lead.products} onChange={set("products")} data-testid="capture-products" /></Field>
              <Field label="Urgency"><Select value={lead.urgency} onValueChange={(v) => setLead((l) => ({ ...l, urgency: v }))}><SelectTrigger data-testid="capture-urgency"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></Field>
            </div>
            <Field label="First next action"><Input value={lead.nextAction} onChange={set("nextAction")} data-testid="capture-next-action" /></Field>
          </div>

          {sources.length > 0 && (
            <div className="panel p-4">
              <div className="section-title mb-2">Sources the AI used</div>
              <ul className="space-y-1">{sources.map((s, i) => <li key={i}><a href={s.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1 break-all"><ExternalLink className="h-3.5 w-3.5 shrink-0" />{s.title || s.url}</a></li>)}</ul>
            </div>
          )}

          <div className="panel p-4 flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
            <div className="flex flex-col sm:flex-row gap-4">
              <label className="flex items-center gap-2 text-sm"><Switch checked={createOpp} onCheckedChange={setCreateOpp} data-testid="capture-create-opp" />Add to pipeline (open an opportunity)</label>
              <div className="min-w-[200px]"><Field label="Link to ICP (optional)"><Select value={icpId} onValueChange={setIcpId}><SelectTrigger data-testid="capture-icp"><SelectValue placeholder="None" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{icps.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select></Field></div>
            </div>
            <Button size="lg" onClick={createLead} disabled={creating || blocked} data-testid="capture-create-button">
              {blocked ? "Blocked by suppression" : creating ? "Importing…" : "Import lead"}{!blocked && !creating && <ArrowRight className="h-4 w-4 ml-2" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
